// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useTierTheme } from './useTierTheme';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastProvider } from './shared/Toast';
import './shared/modern-ux.css';

// ──────────────────────────────────────────────
// ADMIN TEAM — Overwatch access
// ──────────────────────────────────────────────
const ADMIN_TEAM = [
  'J.lutz@miskulintechnologies.com',
  'R.miskulin@miskulintechnologies.com',
];

// ──────────────────────────────────────────────
// LAZY-LOADED PAGES
// ──────────────────────────────────────────────

// Auth pages
const LoginPage          = lazy(() => import('./pages/LoginPage'));
const PasswordChangePage = lazy(() => import('./pages/PasswordChangePage'));
const NDASignaturePage   = lazy(() => import('./pages/NDASignaturePage'));
const AppTutorialPage    = lazy(() => import('./pages/AppTutorialPage'));

// Core pages
const DashboardPage      = lazy(() => import('./pages/DashboardPage'));
const GeneratorPage      = lazy(() => import('./pages/GeneratorPage'));
const GalleryPage        = lazy(() => import('./pages/GalleryPage'));
const MintPage           = lazy(() => import('./pages/MintPage'));
const OverwatchPage      = lazy(() => import('./pages/OverwatchPage'));
const AnalyticsPage      = lazy(() => import('./pages/AnalyticsPage'));
const ProfilePage        = lazy(() => import('./pages/ProfilePage'));
const PrivacyPage        = lazy(() => import('./pages/PrivacyPage'));
const TermsPage          = lazy(() => import('./pages/TermsPage'));

// ──────────────────────────────────────────────
// LOADING SPINNER — Florigen purple
// ──────────────────────────────────────────────
function LoadingSpinner({ full, label }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      height: full ? '100vh' : '60vh', width: '100%', gap: 16,
    }}>
      <div style={{
        width: 40, height: 40, border: '3px solid rgba(124, 58, 237, 0.15)',
        borderTopColor: '#7C3AED', borderRadius: '50%',
        animation: 'florigen-spin 0.8s linear infinite',
      }} />
      {label && <div style={{ color: '#A78BFA', fontSize: 13, fontWeight: 500, letterSpacing: '0.5px' }}>{label}</div>}
      <style>{`@keyframes florigen-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ──────────────────────────────────────────────
// PRIVATE ROUTE — Auth gate
// ──────────────────────────────────────────────
function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner full label="Authenticating..." />;
  return user ? children : <Navigate to="/login" />;
}

// ──────────────────────────────────────────────
// ONBOARDING GATE
// password_changed → nda_signed → tutorial_done
// ──────────────────────────────────────────────
function OnboardingGate({ children }) {
  const { user, onboardingStep, completeOnboardingStep } = useAuth();
  if (!user) return children;
  if (onboardingStep === 'password') {
    return (
      <Suspense fallback={<LoadingSpinner full label="Loading..." />}>
        <PasswordChangePage onComplete={() => completeOnboardingStep('password')} />
      </Suspense>
    );
  }
  if (onboardingStep === 'nda') {
    return (
      <Suspense fallback={<LoadingSpinner full label="Loading..." />}>
        <NDASignaturePage onComplete={() => completeOnboardingStep('nda')} />
      </Suspense>
    );
  }
  if (onboardingStep === 'tutorial') {
    return (
      <Suspense fallback={<LoadingSpinner full label="Loading..." />}>
        <AppTutorialPage onComplete={() => completeOnboardingStep('tutorial')} />
      </Suspense>
    );
  }
  return children;
}

// ──────────────────────────────────────────────
// ADMIN GUARD — Overwatch access check
// ──────────────────────────────────────────────
function AdminRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (!ADMIN_TEAM.includes(user.email?.toLowerCase()) && !ADMIN_TEAM.includes(user.email)) {
    return <Navigate to="/dashboard" />;
  }
  return children;
}

// ──────────────────────────────────────────────
// APP — Route Tree
// ──────────────────────────────────────────────
export default function App() {
  const { user, loading } = useAuth();
  useTierTheme(user);

  if (loading) return <LoadingSpinner full label="Loading Florigen..." />;

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingSpinner full label="Loading..." />}>
        <ToastProvider position="top-right">

        <Routes>
          {/* Public routes */}
          <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* Overwatch — outside Layout, admin-only */}
          <Route path="/overwatch" element={
            <PrivateRoute>
              <AdminRoute><OverwatchPage /></AdminRoute>
            </PrivateRoute>
          } />

          {/* Authenticated routes inside Layout */}
          <Route path="/" element={<PrivateRoute><OnboardingGate><Layout /></OnboardingGate></PrivateRoute>}>
            <Route index element={<Navigate to="/dashboard" />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="generator" element={<ErrorBoundary><GeneratorPage /></ErrorBoundary>} />
            <Route path="gallery" element={<GalleryPage />} />
            <Route path="mint" element={<MintPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="profile" element={<ProfilePage />} />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" />} />
        <Route path="/demo-management" element={<ProtectedRoute><DemoManagementPage /></ProtectedRoute>} />

        </Routes>
        </ToastProvider>
      </Suspense>
    </ErrorBoundary>
  );
}
